import { AreaChartOutlined, RedoOutlined } from '@ant-design/icons';
import {
  ATitle,
  Loading,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { Row } from 'antd';
import { useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { formatDate } from '../../../shared/utils/format-date';
import api from '../../../services/api';
import randomColor from 'randomcolor';
import { IChartPointLogProps, IFormLog } from '../../../shared/interfaces';
import { Line, getElementAtEvent } from 'react-chartjs-2';
import DetailsLog from './DetailsLog';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const FormCharts: React.FC = () => {
  const { client_id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [charts, setCharts] = useState<any | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [chartLog, setChartLog] = useState<IChartPointLogProps | null>(null);
  const [modalDetail, setModalDetail] = useState<boolean>(false);
  const chartRef = useRef<any>();

  const loadForms = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/clients-forms/forms/${client_id}`)
      .then((response) => {
        const { client_name, forms } = response.data;
        setClientName(client_name);
        if (!forms.length) return;
        const array_logs: IFormLog[] = [];
        const array_days: string[] = [];

        forms.map((item: any) => {
          const { data } = item;
          data.forEach((dt: IFormLog) => array_logs.push(dt));
        });

        array_logs.sort(
          (a: IFormLog, b: IFormLog) =>
            new Date(a.date ?? '').valueOf() - new Date(b.date ?? '').valueOf(),
        );
        const datasets = forms.map((item: any) => {
          const { data } = item;
          const borderColor = randomColor({ format: 'rgb' });
          const backgroundColor = borderColor.replace(')', ', 0.5)');
          return {
            label: item.form_title,
            data: data.map((item: IFormLog) => {
              return { y: item.rank, x: formatDate(item.date), ...item };
            }),
            borderColor,
            backgroundColor,
          };
        });

        const initial_date = new Date(array_logs[0].date ?? '');
        const final_date = new Date(
          array_logs[array_logs.length - 1].date ?? '',
        );
        while (initial_date <= final_date) {
          const dateA = initial_date.setDate(initial_date.getDate() + 1);
          array_days.push(
            new Date(dateA).toLocaleDateString('fr-CA', {
              timeZone: 'America/Araguaina',
            }),
          );
        }

        const datasChart = {
          labels: array_days.map((item) => `${formatDate(item)}`) ?? [],
          datasets,
        };
        setCharts(datasChart);
      })
      .catch((error) => {
        setCharts({ labels: [], datasets: [] });
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${error.response?.data.message}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client_id]);

  const renderModalDetailsLog = () => {
    if (modalDetail) {
      return (
        <DetailsLog
          open={modalDetail}
          close={() => setModalDetail(false)}
          log={chartLog}
        />
      );
    }
  };

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (getElementAtEvent(chartRef.current, event).length > 0) {
        const dataset = getElementAtEvent(chartRef.current, event);
        const element: any = dataset[0]?.element;
        setChartLog(element.$context.raw);
        setModalDetail(true);
      }
    },
    [],
  );

  const options: ChartOptions<any> = {
    responsive: true,
    scales: {
      y: {
        max: 10,
        min: 0,
      },
    },
  };

  useEffect(() => {
    loadForms();
  }, []);

  if (loading) return <Loading load />;

  return (
    <PageLayout>
      <PageIntro
        badge="Prontuários"
        icon={<AreaChartOutlined />}
        title="Gráficos gerais"
        description={`Análise visual do prontuário de ${clientName ?? 'paciente'}.`}
      />
      <PagePanel>
        <Row
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <ATitle icon={<AreaChartOutlined />}>Gráficos Gerais {' >'}</ATitle>
          <ATitle color="#87d068">{clientName}</ATitle>
        </Row>
        <Row style={{ display: 'flex', justifyContent: 'end' }}>
          <MenuOptions
            menu_items={[
              {
                label: 'Atualizar',
                key: '1',
                icon: <RedoOutlined />,
                onClick: () => loadForms(),
              },
            ]}
            showButtonBack
          />
        </Row>
        <Row>
          <Line
            ref={chartRef}
            options={options}
            data={charts}
            onClick={handleClick}
          />
        </Row>
        {renderModalDetailsLog()}
      </PagePanel>
    </PageLayout>
  );
};
export default FormCharts;
